import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useAssetsExchange, type ExchangeAssetsRequestDto } from '../api';
import type { ExchangeAsset } from './types';
import { useExchangeConfirmationModal } from './useExchangeConfirmationModal';
import { formatReceivedAmount } from '@/shared/lib';

type ExchangeFieldError = {
  message: string;
  target: 'amount' | 'select';
} | null;

const useExchangeForm = (
  assetsFrom: Array<ExchangeAsset>,
  assetsTo: Array<ExchangeAsset>
) => {
  const { t } = useTranslation();
  const { openExchangeConfirmationModal } = useExchangeConfirmationModal();
  const [formErrors, setFormErrors] = useState<{
    payFieldErrorAfterClick: string | null;
    receiveFieldErrorAfterClick: string | null;
  }>({
    payFieldErrorAfterClick: null,
    receiveFieldErrorAfterClick: null,
  });
  const [amountToReceive, setAmountToReceive] = useState<string>('');
  const [isPayAmountTouched, setIsPayAmountTouched] = useState(false);
  const [isReceiveAmountTouched, setIsReceiveAmountTouched] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<{
    leftPart: string;
    rightPart: string;
  }>({ leftPart: '', rightPart: '' });
  const {
    isExchangeAssetsSuccess,
    isExchangeAssetsError,
    exchangeAssetsError,
  } = useAssetsExchange();

  const form = useForm<ExchangeAssetsRequestDto>({
    mode: 'onChange',
    defaultValues: {
      assetFrom: '',
      assetTo: '',
      amount: '',
    },
  });

  const assetCodeToPay = form.watch('assetFrom');
  const assetCodeToReceive = form.watch('assetTo');
  const amountToPay = form.watch('amount');
  const lastSetAssetToRef = useRef<string>('');

  const onSubmit = (data: ExchangeAssetsRequestDto) => {
    const formErrors = manuallyValidate();
    if (
      !formErrors.payFieldErrorAfterClick &&
      !formErrors.receiveFieldErrorAfterClick
    ) {
      openExchangeConfirmationModal({
        title: t('exchange.confirmationTitle'),
        amountToPay: Number(amountToPay),
        amountToReceive: Number(amountToReceive),
        assetCodeToPay,
        assetCodeToReceive,
        conversionRate: exchangeRate,
        formData: data,
        onSuccess: () => {
          form.reset();
          setAmountToReceive('');
          resetTouchedFields();
        },
      });
    } else {
      setFormErrors(formErrors);
    }
  };

  const payFieldErrorBeforeClick = useMemo<ExchangeFieldError>(() => {
    if (!assetCodeToPay) {
      return isPayAmountTouched && Number(amountToPay) > 0
        ? { message: t('exchange.selectAsset'), target: 'select' }
        : null;
    }

    const selectedAsset = assetsFrom.find(
      asset => asset.code === assetCodeToPay
    );

    if (!selectedAsset) {
      return null;
    }

    const isNotEnoughBalance =
      (selectedAsset.available === undefined && Number(amountToPay) > 0) ||
      (selectedAsset.available !== undefined &&
        Number(amountToPay) > selectedAsset.available);

    if (isNotEnoughBalance) {
      return {
        message: t('exchange.notEnoughBalance', {
          available: formatReceivedAmount(selectedAsset?.available ?? 0),
        }),
        target: 'amount',
      };
    }

    return null;
  }, [amountToPay, assetCodeToPay, assetsFrom, isPayAmountTouched, t]);

  const receiveFieldErrorBeforeClick = useMemo<ExchangeFieldError>(() => {
    if (
      isReceiveAmountTouched &&
      !assetCodeToReceive &&
      Number(amountToReceive) > 0
    ) {
      return { message: t('exchange.selectAsset'), target: 'select' };
    }

    return null;
  }, [amountToReceive, assetCodeToReceive, isReceiveAmountTouched, t]);

  const onPayAmountBlur = useCallback(() => {
    setIsPayAmountTouched(true);
  }, []);

  const onReceiveAmountBlur = useCallback(() => {
    setIsReceiveAmountTouched(true);
  }, []);

  const resetTouchedFields = () => {
    setIsPayAmountTouched(false);
    setIsReceiveAmountTouched(false);
  };

  const manuallyValidate = useCallback(() => {
    // If only one asset is selected but no amount is entered
    if (assetCodeToPay && !assetCodeToReceive && Number(amountToPay) === 0) {
      return {
        payFieldErrorAfterClick: t('authPages.validation.requiredField'),
        receiveFieldErrorAfterClick: t('exchange.assetToReceiveRequired'),
      };
    }

    if (
      !assetCodeToPay &&
      assetCodeToReceive &&
      Number(amountToReceive) === 0
    ) {
      return {
        payFieldErrorAfterClick: t('exchange.assetToPayRequired'),
        receiveFieldErrorAfterClick: t('authPages.validation.requiredField'),
      };
    }

    // If only one asset and it's amount selected but not the other asset
    if (assetCodeToPay && !assetCodeToReceive && Number(amountToPay) > 0) {
      return {
        payFieldErrorAfterClick: null,
        receiveFieldErrorAfterClick: t('authPages.validation.requiredField'),
      };
    }

    if (!assetCodeToPay && assetCodeToReceive && Number(amountToReceive) > 0) {
      return {
        payFieldErrorAfterClick: t('authPages.validation.requiredField'),
        receiveFieldErrorAfterClick: null,
      };
    }

    // If both assets are selected but no amount is entered
    if (assetCodeToPay && assetCodeToReceive && Number(amountToPay) === 0) {
      return {
        payFieldErrorAfterClick: t('authPages.validation.requiredField'),
        receiveFieldErrorAfterClick: t('authPages.validation.requiredField'),
      };
    }

    return {
      payFieldErrorAfterClick: null,
      receiveFieldErrorAfterClick: null,
    };
  }, [amountToPay, amountToReceive, assetCodeToPay, assetCodeToReceive]);

  const resetFormErrors = () => {
    setFormErrors({
      payFieldErrorAfterClick: null,
      receiveFieldErrorAfterClick: null,
    });
  };

  const resetForm = (values?: Partial<ExchangeAssetsRequestDto>) => {
    const newValues = values
      ? { assetFrom: '', assetTo: '', amount: '', ...values }
      : { assetFrom: '', assetTo: '', amount: '' };

    if (newValues.assetTo) {
      lastSetAssetToRef.current = newValues.assetTo;
    }

    form.reset(newValues);
    resetTouchedFields();
  };

  const assetsToCodes = useMemo(
    () => assetsTo.map(asset => asset.code).join(','),
    [assetsTo]
  );

  useEffect(() => {
    if (assetCodeToReceive === lastSetAssetToRef.current) {
      lastSetAssetToRef.current = '';
      return;
    }

    if (
      assetCodeToPay &&
      assetCodeToReceive &&
      !assetsTo.some(asset => asset.code === assetCodeToReceive)
    ) {
      form.setValue('assetTo', '');
    }
  }, [assetsToCodes, assetCodeToPay, assetCodeToReceive, form, assetsTo]);

  const setAssetCodeToPay = useCallback(
    (nextAssetCodeToPay: string) => {
      form.setValue('assetFrom', nextAssetCodeToPay);
    },
    [form]
  );

  const setAssetCodeToReceive = useCallback(
    (nextAssetCodeToReceive: string) => {
      form.setValue('assetTo', nextAssetCodeToReceive);
    },
    [form]
  );

  const setAmountToPay = useCallback(
    (amount: string) => {
      form.setValue('amount', String(amount));
    },
    [form]
  );

  return {
    form,
    assetCodeToPay,
    assetCodeToReceive,
    amountToPay,
    amountToReceive,
    isExchangeAssetsSuccess,
    isExchangeAssetsError,
    exchangeAssetsError,
    payFieldErrorBeforeClick,
    receiveFieldErrorBeforeClick,
    onPayAmountBlur,
    onReceiveAmountBlur,
    formErrors,
    resetFormErrors,
    onSubmit,
    setExchangeRate,
    setAmountToPay,
    setAmountToReceive,
    setAssetCodeToPay,
    setAssetCodeToReceive,
    resetForm,
  };
};

export { useExchangeForm };
